<script setup lang="ts">
import { type Component, computed } from 'vue'

import '@tix/ds/styles/portico/component/badge.css'

import BaseIcon from './BaseIcon.vue'

/** Portico badge type (Figma Type axis). */
export type BadgeType = 'default' | 'success' | 'warning' | 'danger' | 'info'
export type BadgeVariant = 'filled' | 'outlined'
export type BadgeSize = 'md' | 'sm' | 'xs' | '2xs' | '3xs'
export type BadgeCorners = 'pill' | 'rounded'

interface Props {
  label: string
  type?: BadgeType
  variant?: BadgeVariant
  size?: BadgeSize
  corners?: BadgeCorners
  /** Tabler icon component from `@tabler/icons-vue`. */
  iconBefore?: Component
  /** Tabler icon component from `@tabler/icons-vue`. */
  iconAfter?: Component
}

const props = withDefaults(defineProps<Props>(), {
  type: 'default',
  variant: 'filled',
  size: '2xs',
  corners: 'pill',
})

const modeClass = computed(() => (props.size === 'md' ? undefined : `mode-${props.size}`))

const badgeStyle = computed(() => {
  const prefix = `--badge-${props.variant}-${props.type}`
  return {
    background: `var(${prefix}-bg)`,
    color: `var(${prefix}-text)`,
    borderColor: props.variant === 'outlined' ? `var(${prefix}-border)` : 'transparent',
  }
})
</script>

<template>
  <span class="badge" :class="[modeClass, variant, corners]" :style="badgeStyle">
    <BaseIcon v-if="iconBefore" :icon="iconBefore" :stroke="1.5" />
    <span class="label">
      <span class="label-text">{{ label }}</span>
    </span>
    <BaseIcon v-if="iconAfter" :icon="iconAfter" :stroke="1.5" />
  </span>
</template>

<style scoped>
.badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-0-25);
  padding: var(--space-0-25) var(--space-0-5);
  font-family: var(--font-family-body);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;
  border: var(--border-default) solid transparent;
  font-size: var(--space-1);

  &.pill {
    border-radius: var(--radius-full);
  }

  &.rounded {
    border-radius: var(--radius-small);
  }

  .label {
    display: inline-flex;
    align-items: center;
    height: var(--space-em-1-5);
    padding-inline: var(--space-0-25);
  }

  .label-text {
    line-height: 1;
    /* Drop empty font descender / ascender leading so uppercase ink centers with icons. */
    text-box: trim-both cap alphabetic;
  }
}
</style>
